import { Event, UnsignedEvent } from 'nostr-tools';
import { finalizeEvent } from 'nostr-tools/pure';
import { getNostrPool } from '../lib/nostrPool';
import { RELAYS } from '../config/constants';

/**
 * NIP-65 Relay List Service
 * Manage user's relay preferences
 */

interface Relay {
    url: string;
    read: boolean;
    write: boolean;
}

export const relayListService = {
    /**
     * Fetch user's relay list (Kind 10002)
     */
    async fetchRelayList(pubkey: string): Promise<Relay[]> {
        const pool = getNostrPool();
        
        try {
            const events = await pool.querySync(RELAYS, {
                kinds: [10002],
                authors: [pubkey],
                limit: 1,
            });

            if (events.length === 0) return [];

            const latestEvent = events.sort((a, b) => b.created_at - a.created_at)[0];
            
            return latestEvent.tags
                .filter(t => t[0] === 'r')
                .map(t => ({
                    url: t[1],
                    read: !t[2] || t[2] === 'read',
                    write: !t[2] || t[2] === 'write',
                }));
        } catch (error) {
            console.error('Failed to fetch relay list:', error);
            return [];
        }
    },

    /**
     * Publish relay list (Kind 10002)
     */
    async publishRelayList(
        relays: Relay[],
        sk?: Uint8Array | null
    ): Promise<void> {
        const pool = getNostrPool();

        const tags: string[][] = relays.map(r => {
            const tag = ['r', r.url];
            if (r.read && r.write) {
                // Both read and write - no marker
            } else if (r.read) {
                tag.push('read');
            } else if (r.write) {
                tag.push('write');
            }
            return tag;
        });

        const eventTemplate = {
            kind: 10002,
            created_at: Math.floor(Date.now() / 1000),
            tags,
            content: '',
        } as Omit<UnsignedEvent, 'pubkey'>;

        let signedEvent: Event;

        if (sk) {
            const { getPublicKey } = await import('nostr-tools/pure');
            const pubkey = getPublicKey(sk);
            const fullEvent: UnsignedEvent = { ...eventTemplate, pubkey };
            signedEvent = finalizeEvent(fullEvent, sk);
        } else if (window.nostr) {
            const pubkey = await window.nostr.getPublicKey();
            const eventWithPubkey = { ...eventTemplate, pubkey };
            signedEvent = await window.nostr.signEvent(eventWithPubkey);
        } else {
            throw new Error('No signing method available');
        }

        await Promise.allSettled(pool.publish(RELAYS, signedEvent));
    }
};
